import { Box, Button, Chip, Grid, Tab, Tabs, Tooltip } from '@mui/material';
import { DataGrid, GridActionsCellItem, GridColDef, GridEventListener, GridRenderCellParams, GridRowEditStopReasons, GridRowId, GridRowModel, GridRowModes, GridRowModesModel, GridRowsProp, useGridApiRef } from '@mui/x-data-grid';
import React from 'react';
import CustomNoRowsOverlay from '../Common/CustomNoRowsOverlay';
import { formatNumber } from '../Common/FormatNumber';
import Statistic from './Statistics/Statistic';
import TabPanel from '@mui/lab/TabPanel';
import TabContext from '@mui/lab/TabContext';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast, ToastContainer } from 'react-toastify';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import PublishIcon from '@mui/icons-material/Publish';
import UnpublishedIcon from '@mui/icons-material/Unpublished';
import DialogForm from './DialogForm';
import axios from 'axios';


export default function WhisperIndex() {
    const [rows, setRows] = React.useState<GridRowsProp>([]);
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const [loading, setLoading] = React.useState(false);
    const apiRef = useGridApiRef();
    const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>({});
    const [total_r_count, setTotal_r_count] = React.useState(0);
    const [total_t_count, setTotal_t_count] = React.useState(0);
    const [total_p_count, setTotal_p_count] = React.useState(0);
    const [total_o_count, setTotal_o_count] = React.useState(0);
    const [total_corp_count, setTotal_corp_count] = React.useState(0);
    const [value, setValue] = React.useState('R');
    const [whisper_r_stats, setWhisper_r_stats] = React.useState([]);
    const [whisper_t_stats, setWhisper_t_stats] = React.useState([]);
    const [whisper_p_stats, setWhisper_p_stats] = React.useState([]);
    const [whisper_o_stats, setWhisper_o_stats] = React.useState([]);
    const [whisper_corp_stats, setWhisper_corp_stats] = React.useState([]);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const [selectedRow, setSelectedRow] = React.useState<GridRowModel | null>(null);
    const [openDialog, setOpenDialog] = React.useState(false);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };


    const handleChange = (event: React.SyntheticEvent, newValue: string) => {
        if (newValue === 'monthlyconcerns') {
            window.location.href = `${user.role === 'Administrator' ? '/admin' : user.role === 'Executive' ? '/executive' : ''}/whisper/monthly-interests`;
        }else{
            setValue(newValue);
        }
    };
    
    React.useEffect(() => {
        setLoading(true);
        fetch(`/${user.role === 'Administrator' ? 'admin' : user.role === 'Executive' ? 'executive' : ''}/get-whispers/${value}?entity_id=${localStorage.getItem('entity_id')}`)
            .then(response => response.json())
            .then(data => {
                setRows(data.rows);
                setTotal_r_count(data.total_r_count);
                setTotal_t_count(data.total_t_count);
                setTotal_p_count(data.total_p_count);
                setTotal_o_count(data.total_o_count);
                setTotal_corp_count(data.total_corp_count);
                setWhisper_r_stats(data.whisper_r_stats);
                setWhisper_t_stats(data.whisper_t_stats);
                setWhisper_p_stats(data.whisper_p_stats);
                setWhisper_o_stats(data.whisper_o_stats);
                setWhisper_corp_stats(data.whisper_corp_stats);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [value]);

    const handleEditClick = (row: GridRowModel) => () => {
        setOpenDialog(true);
        setSelectedRow(row);
    };

    const handleDeleteClick = async (id: GridRowId) => {
        const updatedRows = rows.filter((row) => row.id !== id);
        setRows(updatedRows);
        const csrfToken = await axios.get('/csrf').then((response) => response.data.csrfToken);
        axios.delete(`/${user.role === 'Administrator' ? 'admin' : user.role === 'Executive' ? 'executive' : ''}/whisper/${id}/delete/?entity_id=${localStorage.getItem('entity_id')}`, {
            headers: {
                'X-CSRFToken': csrfToken,
            },
        }).then((response) => {
            toast.success(response.data.message, { position: 'top-center', autoClose: 2000 });
        }).catch((error) => {
            toast.error(error.response.data.message, { position: 'top-center', autoClose: 2000 });
        });
    };
    
    const handleSaveClick = (id: GridRowId) => () => {
        setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
    };

    const handleCancelClick = (id: GridRowId) => () => {
        setTimeout(() => {
            setRowModesModel({
                ...rowModesModel,
                [id]: { mode: GridRowModes.View, ignoreModifications: true },
            });
        }, 100);
    
        const editedRow = rows.find((row) => row.id === id);
        if (editedRow!.isNew) {
          setRows(rows.filter((row) => row.id !== id));
        }
    };

    const processRowUpdate = async (newRow: GridRowModel) => {
        const updatedRow = { ...newRow, isNew: false };
        setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));
        return updatedRow;
    };

    const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
        if (params.reason === GridRowEditStopReasons.rowFocusOut) {
          event.defaultMuiPrevented = true;
        }
    };

    const handleManageStatusClick = async (id: GridRowId) => {
        const row = rows.find((row) => row.id === id);
        const newStatus = row.status_id === 0 ? 1 : row.status_id === 1 ? 0 : 1;
        apiRef.current?.updateRows([{
            ...row,
            status_id: newStatus,
        }]);
        const csrfToken = await axios.get('/csrf').then((response) => response.data.csrfToken);
        axios.put(`/${user.role === 'Administrator' ? 'admin' : user.role === 'Executive' ? 'executive' : ''}/whisper/${id}/update-status/?entity_id=${localStorage.getItem('entity_id')}`, {
            status_id: newStatus,
        }, {
            headers: {
                'X-CSRFToken': csrfToken,
            },
        }).then((response) => {
            toast.success(response.data.message, { position: 'top-center', autoClose: 2000 });
        }).catch((error) => {
            toast.error(error.response?.data?.message, { position: 'top-center', autoClose: 2000 });
        });
    };

    const handleDuplicateClick = async (id: GridRowId) => {
        const row = rows.find((row) => row.id === id);
        const newRow = { ...row, id: rows.length + 1, isNew: true };
        setRows([...rows, newRow]);
        const csrfToken = await axios.get('/csrf').then((response) => response.data.csrfToken);
        axios.post(`/${user.role === 'Administrator' ? 'admin' : user.role === 'Executive' ? 'executive' : ''}/whisper/${id}/duplicate/?entity_id=${localStorage.getItem('entity_id')}`, {
        }, {
            headers: {
                'X-CSRFToken': csrfToken,
            },
        }).then((response) => {
            toast.success(response.data.message, { position: 'top-center', autoClose: 2000 });
        }).catch((error) => {
            toast.error(error.response?.data?.message, { position: 'top-center', autoClose: 2000 });
        });
    };

    const handleProcessRowUpdateError = React.useCallback((error: Error) => {
        toast.error(error.message);
    }, []);


    const columns: GridColDef[] = [
        // { field: 'id', headerName: 'ID', width: 100, headerAlign: 'center', align: 'center', hideable: true },
        { field: 'cust_id', headerName: 'Cust ID', width: 100, headerAlign: 'center', align: 'center', 
            renderHeader: (params) => {
                return <span style={{ fontWeight: 'bold' }}>{params.colDef.headerName as string}</span>;
            },
        },
        { field: 'customer_name', headerName: 'Customer Name', width: 200, headerAlign: 'center', align: 'center', 
            renderHeader: (params) => {
                return <span style={{ fontWeight: 'bold' }}>{params.colDef.headerName as string}</span>;
            },
        },
        { field: 'rev_impact', headerName: 'Rev Impact', width: 100, headerAlign: 'center', align: 'center', 
            renderHeader: (params) => {
                return <span style={{ fontWeight: 'bold' }}>{params.colDef.headerName as string}</span>;
            },
            renderCell: (params: GridRenderCellParams) => {
                return <span style={{ color: params.value > 0 ? 'green' : 'red' }}>{params.value ? formatNumber(params.value) : '-'}</span>
            },
        },
        { field: 'gp_impact', headerName: 'GP Impact', width: 100, headerAlign: 'center', align: 'center', 
            renderHeader: (params) => {
                return <span style={{ fontWeight: 'bold' }}>{params.colDef.headerName as string}</span>;
            },
            renderCell: (params: GridRenderCellParams) => {
                return <span style={{ color: params.value > 0 ? 'green' : 'red' }}>{params.value ? formatNumber(params.value) : '-'}</span>;
            },
        },
        { field: 'whisper', headerName: 'Whisper', width: 800, headerAlign: 'center', align: 'center', type: 'string',
            renderHeader: (params) => {
                return <span style={{ fontWeight: 'bold' }}>{params.colDef.headerName as string}</span>;
            },
            editable: value === 'C' ? true : false,
            renderCell: (params: GridRenderCellParams) => {
                return <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{params.value}</span>;
            }
        },
        // { field: 'status_id', headerName: 'Status', width: 120, headerAlign: 'center', align: 'center', 
        //     renderCell: (params: GridRenderCellParams) => {
        //         return (
        //         <Tooltip title={params.value === 1 ? 'Available to the users for the SMS process' : params.value === 0 ? 'Created but not available to the users' : 'Unpublished'} placement="top">
        //             <Chip 
        //                 label={params.value === 1 ? 'Published' : params.value === 0 ? 'Draft' : 'Unpublished'} 
        //                 color={params.value === 1 ? 'success' : params.value === 0 ? 'warning' :  'error'} 
        //                 size="small"
        //                 sx={{ fontSize: '0.75rem', borderRadius: '3px !important' }}
        //             />
        //         </Tooltip>
        //         )
        //     }
        // },
        {
            field: 'actions',
            width: 200,
            headerAlign: 'center',
            align: 'center',
            type: 'actions',
            getActions: ({ id, row }) => {
                // if (value === 'C') {
            //     return [
            //         <Tooltip title="Edit">
            //             <GridActionsCellItem icon={<EditIcon />} label="Edit" onClick={handleEditClick(row)} />
            //         </Tooltip>,
            //         <Tooltip title="Delete">
            //             <GridActionsCellItem icon={<DeleteIcon />} label="Delete" onClick={() => handleDeleteClick(id)} />
            //         </Tooltip>,
            //         <Tooltip title="Duplicate">
            //             <GridActionsCellItem icon={<FileCopyIcon />} label="Duplicate" onClick={() => handleDuplicateClick(id)} />
            //         </Tooltip>,
            //         <Tooltip title={row.status_id === 0 || row.status_id === 2 ? 'Publish' : 'Unpublish'}>
            //             {
            //                 row.status_id === 0 || row.status_id === 2 ? (
            //                     <GridActionsCellItem icon={<PublishIcon color="success" />} label="Publish" onClick={() => handleManageStatusClick(id)} />
            //                 ) : (
            //                     <GridActionsCellItem icon={<UnpublishedIcon color="error" />} label="Unpublish" onClick={() => handleManageStatusClick(id)} />
            //                 )
            //             }
            //         </Tooltip>,
            //     ];
            // }else {
            
                return [
                    <Button variant="text" size="small" sx={{ fontSize: '0.75rem', fontFamily: 'Trebuchet MS', textTransform: 'capitalize' }} color="primary" onClick={() => {
                        window.location.href = `/customers/${row.cust_id}?entity_id=${localStorage.getItem('entity_id')}`;
                    }}>
                        Go to customer
                    </Button>    
                ]
            }
        }
    ];

    return (
        <Box sx={{ display: 'flex', width: '100%', height: '100%', justifyContent: 'center', alignItems: 'center' }}>
            <ToastContainer />
            <Grid container spacing={2} sx={{ width: '100%', height: '100%', justifyContent: 'center', alignItems: 'center' }}>
                
                <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                    <TabContext value={value}>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tabs 
                                onChange={handleChange} 
                                aria-label="whisper tabs"
                                value={value}
                            >   
                                <Tab wrapped style={{ textTransform: 'capitalize' }} label="Revenues" value="R" />
                                <Tab wrapped style={{ textTransform: 'capitalize' }} label="Trends" value="T" />
                                <Tab wrapped style={{ textTransform: 'capitalize' }} label="Pricing" value="P" />
                                <Tab wrapped style={{ textTransform: 'capitalize' }} label="Operational" value="O" />
                                {/* <Tab wrapped style={{ textTransform: 'capitalize' }} label="Corporate" value="C" /> */}
                                <Tab wrapped style={{ textTransform: 'capitalize' }} label="Monthly Concerns" value="monthlyconcerns" />
                            </Tabs>
                        </Box>
                        <TabPanel value={value} sx={{ padding: 0, margin: 0 }}>
                            <DataGrid
                                apiRef={apiRef}
                                loading={loading}
                                showToolbar
                                columns={columns}
                                rows={rows}
                                pagination
                                initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 }, rowCount: 0 } }}
                                pageSizeOptions={[5, 10, 20, 50, 100]}
                                slots={{ 
                                    noRowsOverlay: () => <CustomNoRowsOverlay message={'No whispers found'} />
                                }}
                                rowModesModel={rowModesModel}
                                processRowUpdate={processRowUpdate}
                                onProcessRowUpdateError={handleProcessRowUpdateError}
                                onRowModesModelChange={(newRowModesModel: GridRowModesModel) => setRowModesModel(newRowModesModel)}
                                onRowEditStop={handleRowEditStop}
                                editMode="row"
                                sx={{ 
                                    width: '100%', 
                                    height: '100%', 
                                    borderRadius: '0 0 6px 6px',
                                    overflowY: 'auto !important',
                                    overflowX: 'auto !important',
                                    '& .MuiDataGrid-row': {
                                        minHeight: '30px !important',
                                    },
                                    '& .MuiDataGrid-cell': {
                                        whiteSpace: 'normal !important',
                                        lineHeight: 'inherit !important',
                                        display: 'flex',
                                        alignItems: 'center',
                                    },
                                    '& .MuiDataGrid-cell:hover': {
                                        color: 'primary.main',
                                    },
                                    '& .MuiDataGrid-row:hover': {
                                        cursor: 'pointer',
                                    },
                                    '& .MuiDataGrid-row--editing': {
                                        boxShadow: '10px 10px 10px 0px rgba(227, 91, 6, 0.1) !important',
                                    },
                                    '& .MuiDataGrid-columnHeaderTitle': {
                                        whiteSpace: 'normal',
                                        lineHeight: 'normal',
                                    },
                                    '& .MuiDataGrid-columnHeader': {
                                        // Forced to use important since overriding inline styles
                                        height: 'unset !important',
                                    },
                                    fontSize: '0.75rem',
                                }}
                            />
                        </TabPanel>
                    </TabContext>    
                </Grid>
            </Grid>
            { value === 'C' && <DialogForm open={openDialog} onClose={() => setOpenDialog(false)} row={selectedRow} user={user} apiRef={apiRef} /> }
        </Box>
    )
}